<template>
  <div ref="modal" 
    class="cx-modal--full search-modal" 
    role="dialog"
    :class="{'showing-results': showResults}"
    v-trap-focus="{ escFunc: cancel, live: true }"
    >
    <div class="cx-modal__body" role="document">
      <button class="cx-btn cx-modal__close m-search__close" @click.prevent="cancel" :aria-label="$t('action.close')">
        <i class="fas fa-fw fa-times"></i>
      </button>
      <div class="cx-card--notify">
        <div class="cx-wrap--article">
          <form role="search" class="cx-div search-modal__form" :aria-label="$t('action.search')">
            <div class="cx-div--outer m-search__graphic">
              <cx-img
                tag="i"
                class="m-graphic--information"
                imgSrc="/static/graphic/mgto_graphic_travelinfo.png"
              ></cx-img>
            </div>
            <div class="cx-row--line m-search__searchfield">
              <div class="cx-div m-search__input">
                <input v-model="keyword"
                  ref="keywordInput"
                  :aria-label="$t('action.search')"
                  type="text" autocomplete="off" required
                  class="cx-field cx-t20 m-search__input" :class="{'error': error}"
                  autocorrect="off" @keydown.enter="search" 
                  :placeholder="$t('search.placeholder')">
              </div>
              <div class="cx-div">
                <button class="c-button cx-btn--full m-search__btn cx-t20"
                  @click.prevent="search"
                  :disabled="loading"
                >
                  <span class="cx-btn__icon" aria-hidden="true">
                    <i class="fas fa-search"></i>
                  </span>
                  <span>{{$t('action.search')}}</span>
                </button>
              </div>  
            </div>
          </form>
          <div class="search-modal__results" v-if="showResults">
            <div class="cx-div--sm -v-center -j-between" style="display:flex">
              <result-text
              :tag="'div'"
              :num="totalResults"
              :loading="loading"
            ></result-text>
              <rich-search-select
                icon="fas fa-sort-amount-down"
                :name="$t('common.relevance')"
                :options="[
                  {
                    name: $t('common.date'),
                    value: 'date'
                  }
                ]"
                :defaultOptionName="$t('common.relevance')"
                :selectedOption.sync="sort"
                v-on:change="(val) => fetchSearchResult({
                  q: query,
                  sort: val
                })"
              ></rich-search-select>
            </div>
            <div class="cx-div cx-t20" v-if="spelling.length > 0">
              {{
                $t('common.searchSpellingCorrect')
              }}{{
                $t('common.colon')
              }}<a class="cx-link -primary" @click="correctQuery(spelling)"
              >{{spelling}}</a>
            </div>
            <ul class="cx-list cx-list--vertical m-search__resultlist">
              <li class="cx-list__item -left cx-div" v-for="(r, idx) in results"
                :key="idx + '|' + r.link">
                <a target="_blank" :href="r.link" rel="noreferrer">
                  <div class="cx-row--halfgutter cx-row--line m-search__resultitem">
                    <div 
                      :class="`cx-div cx-col-md-${!isMobileView && r.thumbnail ? '10' : '12'}`"
                    >
                      <div class="cx-t20 m-search__title"
                        v-html="r.htmlTitle"
                      ></div>
                      <div class="-t-primary m-search__link"
                        v-html="r.htmlFormattedUrl"
                      ></div>
                      <div class="m-search__snippet" 
                        v-html="r.htmlSnippet"></div>
                    </div>
                    <div class="cx-col-md-2 cx-div" 
                      v-if="!isMobileView && r.thumbnail">
                      <cx-img 
                        tag="div"
                        class="cx-media__img cx-media__img--round-corner m-search__resultthumb "
                        :imgSrc="r.thumbnail"
                      ></cx-img>
                    </div>
                  </div>
                </a>
              </li>
            </ul>
            <div class="cx-div -center" v-if="loading">
              <i class="fas fa-circle-notch fa-spin fa-4x"></i>
            </div>
            <div v-if="show404 || somethingWrong">
              <cx-img 
                tag='i'
                class="m-graphic--404 cx-div"
                imgSrc="/static/graphic/mgto_graphic_404.png"
              ></cx-img>  

              <h1 class="cx-div cx-t20" v-if="somethingWrong">
                {{$t('search.errorMessage')}}
              </h1>
            </div>
          </div>
          <div class="cx-btn" 
            ref="seeMoreBtn"
            v-show="showResults && hasMore && !loading && !show404 && !somethingWrong"
            @click="fetchMoreResult"
          >{{$t('action.seeMore')}}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { PAGE_LOADED } from '../constants/gtmce';
import lazyLoadHelper from '@/mixins/lazyLoadHelper';
import restoreFocus from '@/mixins/restoreFocus';
import ResultText from '@/components/ResultText';
import RichSearchSelect from '@/components/RichSearchSelect';

const SEARCH_URL = '/api/gcse'; 

export default {
  name: 'search-modal',
  mixins: [lazyLoadHelper, restoreFocus],
  uit() {
    return [
      ['common', [
        'orderBy',
        'searchSpellingCorrect',
        'colon',
        'date',
        'relevance'
      ]],
      ['search', [
        'header',
        'placeholder',
        'errorMessage',
      ]],
      ['action', [
        'cancel',
        'search',
        'seeMore',
        'close',
      ]],
    ]
  },
  props: {
    chatbotKeyword: {
      type: String,
      default: '',
    },
  },
  components: {
    ResultText,
    RichSearchSelect
  },
  data() {
    return {
      showResults: false,
      keyword: '',
      query: '',
      sort: '',
      spelling: '',
      results: [],
      error: false,
      loading: false,
      totalResults: 0,
      hasMore: true,
      somethingWrong: false,
    }
  },
  computed: {
    ...mapState({
      isMobileView: state => state.ui.isMobileView
    }),
    show404: function() {
      return !this.loading && this.query != '' && this.results.length == 0;
    },
  },
  methods: {
    resetResult: function({q, sort}) {
      this.query = q || '';
      this.sort = sort || '';
      this.results = [];
      this.totalResults = 0;
      this.hasMore = true;
      this.spelling = '';
    },
    search: function() {
      if (this.loading) return ;
      
      // 避免使用者輸入「前後空白」造成 query/追蹤路徑/搜尋結果不一致
      this.keyword = (this.keyword || '').trim();

      if (this.keyword.length == 0) {
        this.error = true;
        return;
      }
      this.showResults = true;
      this.error = false;

      this.fetchSearchResult({ q: this.keyword });
    },
    correctQuery: function(q) {
      this.keyword = q;      
      this.fetchSearchResult({q})
    },
    handleLazyLoadMore: function() {
      this.hasMore && !this.loading && !this.show404 && this.fetchMoreResult();
    },
    fetchMoreResult: function() {
      this.fetchSearchResult({
        q: this.query,
        start: this.results.length
      })
    },
    fetchSearchResult: function({
      q='',
      sort='',
      start=0,
    }) {
      if (q.length == 0) return;
      let { locale } = this.$i18n;
      const isNewQuery = start == 0;
      isNewQuery && this.resetResult({q, sort});

      this.loading = true;
      this.somethingWrong = false;
      this.$gtmPush({
        ...PAGE_LOADED,
        vtitle: this.$t('action.search'),
        vpath: `/${locale}/search?keyword=${q}&sort=${sort || this.sort}`
      })
      axios.get(SEARCH_URL, {
        params: {
          lang: undefined,
          q, start: start + 1,
          sort: sort || this.sort,
          siteSearch: `https://www.macaotourism.gov.mo/${locale}/`
        }
      }).then(res => {
        let {items=[], spelling={}} = res.data;

        // check if has spelling suggestion
        if (spelling.correctedQuery) {
          let chunks = spelling.correctedQuery.split(' ');
          chunks.pop();
          this.spelling = chunks.join(' ');
        }

        // update return results
        this.hasMore = items.length == 10;
        const appendTitle = `- ${this.$t('footer.contactOffice')}`;
        if (items.length) {
          this.results = this.results.concat(items.map((item) => {
            let metatags = (item.pagemap.metatags || [])[0] || {};
            let ogImg = metatags['og:image'] || '';
            let htmlTitle = item.htmlTitle.split('- ');
            item.htmlTitle.indexOf('- ') > -1 && htmlTitle.pop();
            return {
              ...item,
              htmlTitle: htmlTitle.join('- ').replace(/<b>/g, '<strong>').replace(/<\/b>/g, '</strong>'),
              htmlFormattedUrl: item.htmlFormattedUrl.replace(/<b>/g, '<strong>').replace(/<\/b>/g, '</strong>'),
              htmlSnippet: item.htmlSnippet.replace(/<br>\s/g, '').replace(/<b>/g, '<strong>').replace(/<\/b>/g, '</strong>'),
              thumbnail: (!ogImg || ogImg.endsWith('/') || ogImg.endsWith('/static/ogimage_default.png')) ? '' : ogImg + '?imwidth=240'
            }
          }));
        }

        // update meta data
        if (isNewQuery) {
          let {queries: { request = [{}] }} = res.data;
          this.query = request[0].searchTerms
          this.totalResults = request[0].totalResults;
        }
      })
      .catch(err => {
        this.somethingWrong = true;
        console.log(err);
      })
      .then(() => this.loading = false);
    },
    cancel: function() {
      this.keyword = '';
      this.$store.dispatch('ui/DISMISS_FREEZE_BODY');
      this.$emit('cancel');
    }
  },
  watch: {
    isMobileView: function() {
      this.cancel();
    },
    '$route.path': function() {
      this.cancel();
    },
  },
  mounted() {
    this.$refs.keywordInput && this.$refs.keywordInput.focus();
    this.$store.dispatch('ui/FREEZE_BODY');
    this.lazyLoad(this.$refs.seeMoreBtn, this.handleLazyLoadMore, { keepAlive: true});

    if (this.chatbotKeyword) {
      // chatbot 帶入的 keyword 也一併 trim，避免網址上出現 %20 或搜尋不到
      this.keyword = (this.chatbotKeyword || '').trim();
      this.search();
    }
  },
};
</script>

<style lang="scss">
.search-modal {
  input.error {
    border-color: $color-red;
  }
  .cx-t20{
    @media(max-width: 767px){
      @include set-font(1);
    }
  }
  .cx-card--notify{
    @media(max-width: 767px){
      width: 100%;
    }
  }

  .cx-dropdown__content {
    right: 0 !important;
    left: initial !important;
  }
}
.m-search{
  &__input{
    width: 100%;
  }
  &__close{
    color: $color-white!important;
    font-size: 1rem;
  }
  &__resultlist{
    display: block;
    margin: 1em 0;
    li{
      padding: 1em 0;
      display: block;
      a{
        &:hover{
          text-decoration: none;
        }
      }
    }
  }
  &__resultthumb{
    width: 100%;
    padding-top: 100%;
    @media(max-width:767px){
      display: none;
    }
  }
  &__searchbtn{
    padding: 2rem;
  }
  &__searchfield{
    color: $color-black;
    flex-wrap: wrap;
    justify-content: center;
  }
  &__snippet{
    padding: .5em 0;
    // & > br {
    //   display: none;
    // }
  }
  &__title{
    // font-weight: initial;
    & > strong {
      color: darken($color-midgrey, 25% );
    }
  }
  &__link{
    color: $color-midgrey;
    word-wrap: break-word;
  }
  &__resultitem{
    align-items: flex-start;
    .m-search__snippet{
      color: $color-black;
    }
    &:hover{
      .m-search__snippet{
        color: $color-black;
      }
      .m-search__title{
        & > strong {
          color: $color-green;
        }
      }
    }
  }
}

.showing-results{
  .m-search__graphic{
    display: none;
  }
  .m-search__input{
    margin-bottom:  0!important;
    padding-right: $space-sm;
    }
  .m-search__searchfield{
    flex-wrap: nowrap;
    justify-content: left;
    @media(max-width:767px){
      margin-top: 50px;
      .m-search__btn{
        span:last-child(){
          display: none;
        }
      }
    }
    
  }
  .cx-modal__body{
    // overflow: scroll;
    align-items: flex-start;
  }
}
.search-modal {
  position: fixed;
  z-index: 10003 !important;
}
</style>
